export const LEGAL_LINKS = [
  { label: "Support", href: "/support.html" },
  { label: "Platform & Service Disclaimer", href: "/platform-disclaimer.html" },
  { label: "Privacy Policy", href: "/privacy-policy.html" },
  { label: "Terms of Service", href: "/terms.html" },
  { label: "Refund Policy", href: "/refund-policy.html" },
  { label: "Acceptable Use", href: "/acceptable-use.html" },
  { label: "EULA", href: "/eula.html" },
  { label: "Legal Disclaimer", href: "/legal.html" },
];

export const PLATFORM_DOC = {
  key: "platform",
  title: "Platform & Service Disclaimer",
  subtitle: "How third-party services relate to this app — please read first.",
  updated: "October 2026",
  blocks: [
    { t: "p", text: "I am the sole owner and operator of this application, Ramiz Yadkoo." },
    { t: "p", text: "This application may rely on third-party infrastructure, cloud services, APIs, and other upstream service providers to support certain features and functionality. While reasonable efforts are made to ensure reliability and availability, some services are dependent on external providers that operate independently of this application." },
    { t: "p", text: "In the event of a disruption, outage, delay, or technical issue originating from an upstream service provider, certain features of the application may be affected until the provider restores normal operation." },
    { t: "p", text: "I will make reasonable efforts to assist users and maintain the application; however, issues caused by external providers may be outside my direct control and may require resolution by the relevant provider." },
    { t: "p", text: "By using this application, you acknowledge that some functionality may depend on third-party services and that service availability may be affected by factors beyond the application's direct control." },
  ],
};

export const SUPPORT_DOC = {
  key: "support",
  title: "Support",
  subtitle: "Help with your account, refunds, bug reports, or security concerns.",
  blocks: [
    { t: "p", text: "If you need help with your account, refunds, bug reports, or security concerns, you can contact us through the in-app Support Inbox or email us directly:" },
    { t: "email", email: "support@warrantyvaultforyou.com" },
    { t: "note", text: "The Warranty_Vault pro app also includes a built-in Support Inbox with ticket tracking, escalation, and AI-assisted responses." },
    { t: "h", text: "Legal & policy information" },
    { t: "links", items: [
      { label: "Platform & Service Disclaimer", href: "/platform-disclaimer.html" },
      { label: "Privacy Policy", href: "/privacy-policy.html" },
      { label: "Terms of Service", href: "/terms.html" },
      { label: "Refund Policy", href: "/refund-policy.html" },
      { label: "Acceptable Use Policy", href: "/acceptable-use.html" },
      { label: "End-User License Agreement", href: "/eula.html" },
      { label: "Legal Disclaimer", href: "/legal.html" },
    ]},
    { t: "p", text: "We aim to respond to all support requests as quickly as possible." },
  ],
};

export const PRIVACY_DOC = {
  key: "privacy",
  title: "Privacy Policy",
  subtitle: "How your data is collected, stored, and used.",
  updated: "October 2026",
  blocks: [
    { t: "note", text: "Who is responsible for your data — Warranty_Vault pro is operated by Ramiz Yadkoo (\"we\", \"us\"), based in Australia. We handle your personal information in line with the Australian Privacy Act 1988 and the Australian Privacy Principles (APPs). If you are in the EU/UK or California, the additional rights in section 9 also apply to you." },
    { t: "h", text: "1. Data We Collect" },
    { t: "p", text: "To run your account and keep it secure, we collect:" },
    { t: "ul", items: [
      "Account information (name, email, username)",
      "Plan and purchase information (payments are processed by Stripe; we never store your card number)",
      "Content you upload — receipts, warranties, documents and images — stored so we can provide the Service to you",
      "Device identifiers, IP address, device/browser type and sign-in times — stored with your login sessions and used only for account security (sign-in alerts, trusted devices) and to detect account sharing",
      "Usage analytics to improve the app",
      "Support messages you send us",
      "Security logs",
    ]},
    { t: "h", text: "2. How We Use Your Data" },
    { t: "ul", items: [
      "Operate your account and deliver the Service",
      "Security and fraud prevention — powering sign-in alerts, trusted devices, session management and our single-user / plan-limit rules",
      "Provide customer support when you ask for it",
      "Improve the Service",
    ]},
    { t: "h", text: "3. Your Content Stays Private" },
    { t: "p", text: "Your vault content (receipts, images, documents and vault chat) is private to you. The app owner cannot open it unless you explicitly grant permission from inside the app, and you can review, approve, deny or revoke that access at any time." },
    { t: "h", text: "4. Data Sharing" },
    { t: "p", text: "We do not sell your data. We share it only with:" },
    { t: "ul", items: [
      "Essential service providers that run the app (email delivery, push notifications, hosting and file storage)",
      "Stripe, to process your payment when you buy a plan",
      "Legal authorities where required by law",
    ]},
    { t: "h", text: "5. Data Security" },
    { t: "p", text: "We use industry-standard encryption and access controls. Two-factor authentication, trusted-device controls and sign-in alerts help protect your account. You are responsible for keeping a strong password and protecting your devices." },
    { t: "h", text: "6. Data Deletion" },
    { t: "p", text: "You can request deletion of your account and data at any time. Some limited metadata may be retained where necessary for security or legal compliance." },
    { t: "h", text: "7. Children's Privacy" },
    { t: "p", text: "The Service is not available to children under 13; we do not knowingly collect their data, and any such account is removed. Users aged 13–17 may use the Service only with a parent or guardian's consent, which we ask you to confirm (together with a guardian email) when you create your account." },
    { t: "h", text: "8. Updates" },
    { t: "p", text: "We may update this Privacy Policy from time to time. Material changes will require you to re-accept before continuing to use the Service." },
    { t: "h", text: "9. Your Rights" },
    { t: "p", text: "Wherever you live, you can ask us to:" },
    { t: "ul", items: [
      "Access the personal information we hold about you",
      "Correct information that is wrong or out of date",
      "Delete your account and personal data (in-app: Settings → Delete my account)",
      "Export a copy of your data (in-app: Settings → Export & backup — CSV or PDF)",
      "Withdraw consent or object to certain processing",
    ]},
    { t: "p", text: "We do not sell your personal information. If you are in the EU/UK, you also have GDPR rights including restriction and data portability; if you are in California, you have CCPA rights including the right to know and to opt out of any \"sale\" (we do not sell). You can complain to a regulator — in Australia, the Office of the Australian Information Commissioner (OAIC); elsewhere, your local data protection authority." },
    { t: "h", text: "10. Data Retention" },
    { t: "p", text: "We keep your account data for as long as your account is active. When you delete your account your content is removed, though limited metadata (for example security logs or records we must keep to meet legal, tax or fraud-prevention obligations) may be retained for a reasonable period." },
    { t: "h", text: "11. International Transfers" },
    { t: "p", text: "Our hosting, file storage, email and push-notification providers may process data on servers located outside Australia. Where that happens, we take reasonable steps to ensure your information stays protected to a standard consistent with the Australian Privacy Principles." },
    { t: "h", text: "12. Contact" },
    { t: "p", text: "For any privacy request or question, contact us through the in-app Support Inbox or the support contact listed on warrantyvaultforyou.com. We will respond within a reasonable time." },
  ],
};

export const TERMS_DOC = {
  key: "terms",
  title: "Terms of Service",
  subtitle: "The agreement that governs your use of Warranty_Vault pro.",
  updated: "October 2026",
  blocks: [
    { t: "note", text: "Cheating & abuse are strictly prohibited — Hacking, exploiting, using scripts/bots, accessing paid features without paying, manipulating shared vaults, or abusing team accounts can result in a permanent ban without refund — even on lifetime plans. Continued use of the app means you agree to these rules." },
    { t: "h", text: "1. Introduction" },
    { t: "p", text: "By creating an account, accessing, or using this application (\"the Service\"), the user agrees to these Terms of Service. These terms apply to all users, including free-tier, lifetime-tier, Signature Edition, Elite Edition, and Elite Jumbo customers." },
    { t: "h", text: "2. One-Time Payment Policy, Free Trial & Refunds" },
    { t: "p", text: "You can start a free, full-access trial without entering any payment details; no money is charged and the app simply returns to the free plan when the trial ends. All paid plans are one-time payments that unlock lifetime access immediately. If you change your mind, you can request a full refund within 7 days of purchase from inside the app (Settings → Refunds & cancellation). After the 7-day window, a refund may still be granted if:" },
    { t: "ul", items: [
      "A major, critical, and verifiable bug prevents the user from accessing the app or using essential core features, and",
      "The issue cannot be resolved by support within a reasonable timeframe.",
    ]},
    { t: "p", text: "Minor bugs, cosmetic issues, or feature requests do not automatically qualify after the 7-day window. Eligible users must contact support through the in-app Support Inbox. Nothing in this policy limits your rights under the Australian Consumer Law." },
    { t: "h", text: "3. Abuse, Cheating, and Unauthorized Activity" },
    { t: "p", text: "Users must not: attempt to hack, exploit, or manipulate the system; use scripts, bots, automation, or unauthorized tools; bypass account limits, storage limits, or paid features; share paid access with unauthorized individuals; or reverse-engineer or copy the platform. Permanent Ban Policy: users found cheating, hacking, or abusing may be permanently banned, all access revoked, and no refund issued — even on a lifetime plan. This rule is strict and non-negotiable." },
    { t: "h", text: "4. User Responsibilities" },
    { t: "ul", items: [
      "Provide accurate information",
      "Maintain the security of the account",
      "Do not impersonate others",
      "Do not upload harmful, illegal, or malicious content",
      "Do not misuse shared vaults or team features",
    ]},
    { t: "h", text: "4a. Account Sharing Prohibition" },
    { t: "p", text: "All accounts are strictly single-user unless the plan explicitly includes multiple seats (for example Family, Business, and Elite plans — where each seat is a separate, individual account). Sharing an account is prohibited, including: sharing login credentials or passwords; simultaneous logins from multiple devices or locations; using automation to bypass device limits; allowing unauthorized users to access paid features." },
    { t: "p", text: "Consequences of account sharing:" },
    { t: "ul", items: [
      "First offense: warning",
      "Second offense: temporary suspension",
      "Third offense: permanent ban",
      "No refund will be issued for bans caused by account sharing — including lifetime plans",
    ]},
    { t: "h", text: "5. Support & Issue Resolution" },
    { t: "p", text: "Support is available through the in-app Support Inbox. Users must cooperate with troubleshooting. If a user refuses to provide required information or verification, support may be limited." },
    { t: "h", text: "6. Service Availability" },
    { t: "p", text: "The Service is provided \"as-is\" and \"as available.\" We may perform maintenance/updates at any time. We are not responsible for internet outages, device issues, or third-party service interruptions (e.g., email providers, push notification services, OCR engines)." },
    { t: "h", text: "7. Data & Privacy" },
    { t: "p", text: "Users are responsible for the accuracy of uploaded data. We do not guarantee recovery of deleted data. We may store metadata for security, analytics, and fraud prevention." },
    { t: "h", text: "8. White-Label & Business Tier Rules" },
    { t: "p", text: "For Signature Edition, Elite Edition, and Elite Jumbo: the owner is responsible for their team members; misuse by team members may suspend the entire tenant; custom domains must not be used for illegal/harmful activity; abuse from any account under the tenant may result in termination without refund." },
    { t: "h", text: "9. Termination" },
    { t: "p", text: "We may suspend or terminate accounts that violate these Terms. Termination due to misconduct does not qualify for refunds." },
    { t: "h", text: "10. Changes to Terms" },
    { t: "p", text: "We may update these Terms at any time. Continued use means acceptance of the updated Terms." },
    { t: "h", text: "11. Contact" },
    { t: "p", text: "Users can contact support through the in-app Support Inbox for refund eligibility review, bug reporting, account issues, and security concerns." },
  ],
};

export const REFUND_DOC = {
  key: "refund",
  title: "Refund Policy",
  subtitle: "When refunds may or may not be granted.",
  updated: "October 2026",
  blocks: [
    { t: "note", text: "Try free first, and a 7-day money-back guarantee — Start a free trial before you pay. If you do buy, you can request a full refund within 7 days from inside the app — no questions asked. This does not limit your rights under the Australian Consumer Law." },
    { t: "h", text: "1. Free Trial & One-Time Payment Policy" },
    { t: "p", text: "You can start a free, full-access trial with no payment details and nothing to cancel — the app returns to the free plan when the trial ends. All paid plans (including Pro Lifetime, Family Lifetime, Signature Edition, Elite Edition, and Elite Jumbo) are one-time payments that deliver lifetime access immediately upon purchase." },
    { t: "h", text: "2. 7-Day Money-Back Guarantee" },
    { t: "p", text: "If you change your mind, you can request a full refund within 7 days of purchase directly in the app under Settings → Refunds & cancellation; your plan returns to free once refunded, and App Store purchases are refunded by Apple. After the 7-day window, a refund may still be granted if a major, critical, and verifiable bug prevents you from accessing the app or using essential core features and support cannot resolve it within a reasonable timeframe. Cosmetic issues, minor bugs, feature requests, or user error do not automatically qualify." },
    { t: "h", text: "3. Support Requirement" },
    { t: "p", text: "Users must contact support through the in-app Support Inbox and cooperate with troubleshooting steps. If the user refuses to provide required information, a refund cannot be granted." },
    { t: "h", text: "4. No Refunds for Violations" },
    { t: "p", text: "Refunds will not be issued if the user is banned for cheating, hacking, exploiting, abusing the system, account sharing, or violating the Terms of Service — including on lifetime plans." },
    { t: "h", text: "5. Chargebacks" },
    { t: "p", text: "Initiating a fraudulent chargeback may result in permanent account termination, loss of access, legal action, and reporting to payment processors." },
    { t: "h", text: "6. Your Statutory Rights" },
    { t: "p", text: "This policy does not limit rights you cannot waive by law. In Australia, our products come with consumer guarantees that cannot be excluded under the Australian Consumer Law: if there is a major failure you may be entitled to a refund or replacement, and to compensation for any other reasonably foreseeable loss. Consumers in the EU/UK and other regions keep any mandatory rights that apply to them (for example a statutory withdrawal period for digital purchases). Purchases made through the Apple App Store or Google Play can also be refunded directly through that store under its own policy." },
  ],
};

export const ACCEPTABLE_DOC = {
  key: "acceptable",
  title: "Acceptable Use Policy",
  subtitle: "How to behave with social & company collaboration features.",
  updated: "October 2026",
  blocks: [
    { t: "h", text: "1. Respectful Behavior" },
    { t: "p", text: "No harassment, hate, threats, or abusive content." },
    { t: "h", text: "2. Illegal or Harmful Content" },
    { t: "p", text: "No illegal content, malware, spam, fraudulent receipts, or rights-infringing material." },
    { t: "h", text: "3. No Impersonation" },
    { t: "p", text: "Usernames and company @handles must not impersonate others. @handles share one global namespace and must be unique." },
    { t: "h", text: "4. Privacy & Security" },
    { t: "p", text: "Do not access vaults, accounts, or data that are not yours. Do not share your login credentials. Do not attempt to bypass single-user limits." },
    { t: "h", text: "5. Reporting & Enforcement" },
    { t: "p", text: "You can block and report others. We may remove content, issue timeouts, restrict features, or ban accounts." },
    { t: "h", text: "6. Consequences" },
    { t: "p", text: "Violations may result in warnings, temporary timeouts, feature restrictions, or permanent termination. Termination for misconduct does not qualify for a refund." },
  ],
};

export const EULA_DOC = {
  key: "eula",
  title: "End-User License Agreement",
  subtitle: "Your license to use the Service.",
  updated: "October 2026",
  blocks: [
    { t: "h", text: "1. License Grant" },
    { t: "p", text: "Users receive a non-transferable, non-exclusive license to use the Service for personal or business purposes according to their plan." },
    { t: "h", text: "2. Restrictions" },
    { t: "p", text: "Users must not:" },
    { t: "ul", items: [
      "Reverse-engineer the app",
      "Copy or redistribute the software",
      "Bypass account limits",
      "Share your account, login, or password with any other person",
      "Log in from multiple devices simultaneously to bypass single-user limits",
      "Use automation, bots, or scripts",
      "Attempt to hack or exploit the system",
    ]},
    { t: "h", text: "2a. Single-User License" },
    { t: "p", text: "Each license is granted to a single individual. Unless your plan explicitly includes multiple seats (where each seat is a separate account), the account may not be shared. The system may register a limited number of devices per account and may detect and act on simultaneous logins from different devices or locations." },
    { t: "h", text: "3. Ownership" },
    { t: "p", text: "All intellectual property remains the property of the Service provider." },
    { t: "h", text: "4. Termination" },
    { t: "p", text: "We may terminate accounts for violating the Terms, cheating, hacking, fraud, abuse, or account sharing. Termination due to misconduct does not qualify for refunds." },
    { t: "h", text: "5. Updates" },
    { t: "p", text: "We may update or modify the Service at any time." },
    { t: "h", text: "6. Liability" },
    { t: "p", text: "We are not responsible for data loss, device issues, third-party failures, or misuse of the Service." },
    { t: "h", text: "7. Acceptance" },
    { t: "p", text: "By using the Service, the user agrees to this EULA." },
  ],
};

export const LEGAL_DOC = {
  key: "legal",
  title: "Legal Disclaimer",
  subtitle: "Provided \"as-is\" and \"as available.\"",
  updated: "October 2026",
  blocks: [
    { t: "h", text: "1. No Guarantees" },
    { t: "p", text: "We do not guarantee uninterrupted access, error-free performance, compatibility with all devices, or the availability of third-party services." },
    { t: "h", text: "2. Limitation of Liability" },
    { t: "p", text: "We are not liable for data loss, device issues, internet outages, third-party failures (email, push notifications, OCR, hosting providers), user mistakes, or misuse of features." },
    { t: "h", text: "3. User Responsibility" },
    { t: "p", text: "Users are responsible for maintaining their device, securing their account, ensuring their data is backed up, and complying with all laws in their region." },
    { t: "h", text: "4. No Professional Advice" },
    { t: "p", text: "The Service does not provide legal, financial, or professional advice." },
    { t: "h", text: "5. Changes" },
    { t: "p", text: "We may update features, pricing, or policies at any time." },
  ],
};

export const DELETE_ACCOUNT_DOC = {
  key: "delete-account",
  title: "Delete Your Account",
  subtitle: "Permanently delete your Warranty_Vault pro account.",
  blocks: [
    { t: "p", text: "If you would like to permanently delete your Warranty_Vault pro account, you can request deletion using the steps below." },
    { t: "h", text: "How to request account deletion" },
    { t: "p", text: "Email our support team at:" },
    { t: "email", email: "support@warrantyvaultforyou.com" },
    { t: "p", text: "Include the subject line: \u201CDelete my account\u201D" },
    { t: "p", text: "We will verify your identity and permanently delete your account." },
    { t: "h", text: "What will be deleted" },
    { t: "ul", items: [
      "Your account (name, email, username, avatar)",
      "All receipts and uploaded images",
      "All warranties, notes, vaults, checklists",
      "All social posts, comments, messages",
      "All support messages",
      "All login sessions and device IDs",
      "All notifications",
      "All company/team data",
      "All purchase history",
    ]},
    { t: "h", text: "Retention" },
    { t: "p", text: "No data is kept after deletion. Deletion is permanent and cannot be undone." },
    { t: "cta", label: "Contact Support", href: "/support.html" },
  ],
};

export const DELETE_DATA_DOC = {
  key: "delete-data",
  title: "Delete Your Data",
  subtitle: "Delete individual items without deleting your whole account.",
  blocks: [
    { t: "p", text: "You can delete individual data items inside the Warranty_Vault pro app without deleting your entire account." },
    { t: "h", text: "Data you can delete yourself" },
    { t: "ul", items: [
      "Receipts", "Uploaded photos/documents", "Notes", "Warranties", "Vaults", "Checklists",
      "Social posts & comments", "Direct messages", "Team/vault chat messages", "Notifications",
      "Company updates (if applicable)", "Devices/sessions (Trusted Devices)",
    ]},
    { t: "h", text: "Request additional data deletion" },
    { t: "p", text: "If you want us to delete additional data for you, email:" },
    { t: "email", email: "support@warrantyvaultforyou.com" },
    { t: "p", text: "Use the subject: \u201CDelete my data\u201D" },
    { t: "h", text: "Data not deleted unless you delete your account" },
    { t: "ul", items: ["Login credentials", "Profile photo", "Purchase history", "Company/team memberships"] },
    { t: "cta", label: "Contact Support", href: "/support.html" },
  ],
};
